import { ThemeName } from '@ps-int/general-themes';
import { getCurrentTheme, isThemeAvailable } from '@ps-int/general-themes/settings/helpers';

import { isMeetingRoomEmbed, isMessengerEmbed, isSferusEmbed } from '../../../../../common/helpers';
import { RootState } from '../../../../init-store/index';
import { ConferenceStatus } from '../../../pages/call/constants';
import { MediaPlatform, TelemostAgent } from '../../../types';
import { isInsideIframe } from '../../helpers/iframe';
import { getPageClassName } from '../../helpers/theme';
import { browserNameSelector, isMobileUa } from '../uatraits/selectors';
import { isYandexSsoUserSelector, isYandexTeamUserSelector } from '../user/selectors';

const { themes } = require('@ps-int/general-themes/themes');

const LIGHT_THEMES = ['ps-light', 'ps-light-transparent'];

const RECORD_LIMIT = 30 * 60;

// для некоторых браузеров снимаем ограничение записи
const NO_LIMIT_BROWSERS = new Set(['YandexBrowser']);

export const getConfig = (state: RootState) => state.config;

/**
 * @param {RootState} state
 * @returns {Object}
 */
export const getExperimentsFlags = ({ config: { experiments } }: RootState) => experiments?.flags;

export const selectIsTeleMessenger = (state: RootState) => state.config.isTeleMessenger ?? false;

export const isMessengerIntegration = (state: RootState) => {
    return isMessengerEmbed(state.config.embed);
};

export const selectIsMeetingRoomIntegration = (state: RootState) => {
    return isMeetingRoomEmbed(state.config.embed);
};

export const selectIsSferusIntegration = (state: RootState) => {
    return isSferusEmbed(state.config.embed);
};

export const getIsMediaDevicesSyncEnabled = (state: RootState) => {
    return isInsideIframe() && getConfig(state).syncMediaDevices;
};

export const getIsPMScreenRecordingEnabled = (state: RootState) => {
    return isInsideIframe() && getConfig(state).pmScreenRecording;
};

export const getScreenRecordingLimit = (state: RootState) => {
    if (isMessengerIntegration(state) && getConfig(state).screenRecordingLimit !== undefined) {
        return getConfig(state).screenRecordingLimit;
    }

    const browserName = browserNameSelector(state);

    if (NO_LIMIT_BROWSERS.has(browserName)) {
        return 0;
    }

    return RECORD_LIMIT;
};

export const getHideRecordingLimitPopup = (state: RootState) => {
    return getConfig(state).hideRecordingLimitPopup;
};

export const getIsOneOnOneEnabled = (state: RootState) => {
    return getConfig(state).isOneOnOne;
};

export const getIsDisableLocalRecording = (state: RootState) => {
    return getConfig(state).disableLocalRecording && !getIsPMScreenRecordingEnabled(state);
};

export const isAddMembersButtonDisabled = (state: RootState) => {
    return state.config.disableAddMembersButton;
};

export const getIsKickEnabled = (state: RootState) => !isMessengerIntegration(state);

export const getThemeName = (state: RootState) => {
    if (isMessengerIntegration(state) || selectIsTeleMessenger(state)) {
        return null;
    }

    const {
        config: { psSettings, serverTime },
    } = state;

    if (psSettings?.generalThemes) {
        const themeName = getCurrentTheme(psSettings?.generalThemes, new Date(serverTime));
        return themeName && themes[themeName] ? themeName : ThemeName.GeneralDefault;
    }
    return ThemeName.GeneralDefault;
};

export const getCover = (state: RootState) => {
    const themeName = getThemeName(state);
    return (themeName && themes[themeName]?.layers?.cover) || null;
};

export const getPageClassNameSelector = (state: RootState) => {
    const themeName = isMessengerIntegration(state) || selectIsTeleMessenger(state) ? 'ps-dark' : getThemeName(state);

    return getPageClassName(themeName);
};

export const isCoverLayerEnabled = (state: RootState) => {
    const {
        ua: { isMobile },
    } = state;
    return !isMobile && Boolean(getCover(state));
};
export const isNewYearThemeAvailable = (state: RootState) => {
    const {
        config: { serverTime },
    } = state;
    return isThemeAvailable(ThemeName.GeneralNewYear2024, new Date(serverTime));
};

export const isLightTheme = (state: RootState) => {
    const themeName = getThemeName(state);
    const pageTheme = themeName && themes[themeName].page.base.root;

    if (pageTheme) {
        return LIGHT_THEMES.includes(pageTheme);
    }

    return true;
};

export const isMessengerIntegrationTier = (state: RootState, tier: number) => {
    const { messengerIntegrationTier } = state.config;

    if (typeof messengerIntegrationTier !== 'number') {
        return false;
    }

    return messengerIntegrationTier >= tier;
};

export const getBodyThemeName = (state: RootState, path: string) => {
    const {
        config: { errorPageType },
        call: {
            conference: { conferenceStatus, joinedAsGuest },
        },
    } = state;
    const isAutoJoin = getAutoJoin(state);
    const isJoining = conferenceStatus === ConferenceStatus.JOINING;
    const isAutoJoining = isAutoJoin && isJoining;
    const isJoined = conferenceStatus === ConferenceStatus.JOINED;
    const isCreatingCall = !joinedAsGuest;
    const isOnCreateBroadcastPage = path?.startsWith('/create-broadcast');

    if (
        isAutoJoining ||
        isJoined ||
        (isCreatingCall && !isOnCreateBroadcastPage) ||
        (path?.startsWith('/live/') && !errorPageType) ||
        isMessengerIntegration(state) ||
        selectIsTeleMessenger(state)
    ) {
        return 'ps-dark';
    }

    return getThemeName(state);
};

export const getThemeSettings = (state: RootState) => {
    const {
        config: { psSettings },
    } = state;

    if (isCoverLayerEnabled(state)) {
        return psSettings?.generalThemes?.settings;
    }
};

export const getIsDesktopApp = (state: RootState) => state.config.isDesktopApp;

export const getAutoJoin = (state: RootState) => state.config.autoJoin;

export const getGuestName = (state: RootState) => state.config.guestName;

export const getMediaPlatform = (state: Partial<RootState>): MediaPlatform => {
    return state.config?.conferenceMediaPlatform || MediaPlatform.GOLOOM;
};

export const getTelemostAgent = (state: RootState): TelemostAgent => {
    if (isRoomAgentMode(state)) {
        return 'room-agent';
    }

    if (selectIsTranslatorIntegration(state)) {
        return 'translator';
    }

    if (isMessengerIntegration(state) || selectIsTeleMessenger(state)) {
        return 'telemost-messenger';
    }

    if (selectIsMeetingRoomIntegration(state)) {
        return 'meeting-room';
    }

    if (isInsideIframe()) {
        return 'telemost-iframe';
    }

    return 'telemost';
};

export const getRoomAgentToken = (state: RootState) => state.config.roomAgent?.token;

export const isRoomAgentMode = (state: RootState) => Boolean(state.config.roomAgent);

export const getRoomAgentConfig = (state: RootState) => state.config.roomAgent;

export const selectSyncStateWithPeerId = (state: RootState) => state.config.roomAgent?.syncStateWithPeerId;

export const isMultipleStreamsSupportedSelector = (state: RootState): boolean => {
    // Если в isMultipleStreamsSupported значение undefined, то возвращаем true,
    // чтобы не блокировать возможную функциональность, так как значение получаем из goloom-sdk
    return state.config.webRtcCapabilities?.isMultipleStreamsSupported ?? true;
};

export const isNoiseCancellationSupportedSelector = (state: RootState): boolean => {
    return state.config.webRtcCapabilities?.isAudioProcessingSupported ?? true;
};

export const isProductionEnvironmentSelector = (state: RootState): boolean => {
    return state.config.environment === 'production';
};

export const isDevelopmentEnvironmentSelector = (state: RootState): boolean => {
    return state.config.environment === 'development';
};

export const isWithoutRedirect = (state: RootState) => state.config.withoutRedirect;

export const getCreatedWithReload = (state: RootState) => state.config.createdWithReload;

export const getAppVersionSelector = (state: RootState) => state.config.appVersion;

export const selectErrorPageType = (state: RootState) => state.config.errorPageType;

export const selectErrorPageReason = (state: RootState) => state.config.errorPageReason;

export const selectShowUserMismatchesOrganization = (state: RootState) => state.config.showUserMismatchesOrganization;

export const selectTelemostHost = (state: RootState) =>
    state.config.ruTelemostConferenceHost ?? state.config.telemostHost;

export const selectCookierHost = (state: RootState) => state.config.cookierHost;

export const selectHideParticipantName = (state: RootState) => state.config.hideParticipantName;

export const selectHideNonCallsRelatedInterface = (state: RootState) => state.config.hideNonCallsRelatedInterface;

export const selectIsB2BHost = (state: RootState) => {
    return state.config.isB2BHost;
};

export const selectIsDebugToolsEnabled = (state: RootState) =>
    !isProductionEnvironmentSelector(state) ||
    isYandexSsoUserSelector(state) ||
    isYandexTeamUserSelector(state) ||
    selectIsYandexNet(state);

export const selectIsTranslatorIntegration = (state: RootState) => Boolean(state.config.translatorToken);

export const selectIsGoloomRc = (state: RootState) => {
    return Boolean(state.config.isGoloomRc);
};

export const selectIsOnPremise = (state: RootState) => state.config.isOnPremise;

export const selectYaServices = (state: RootState) => {
    return state.config.yaServices;
};

export const selectConferenceBackground = (state: RootState) => state.config.conferenceBackground;

export const selectAvatarsOrigin = (state: RootState) => state.config.avatarsOrigin;

export const selectConfigDevices = (state: RootState) => state.config.devices;

export const selectShouldWaitAccept = (state: RootState) => state.config.waitAccept;

export const selectIsGlobalBarEnabled = (state: RootState) => !isMobileUa(state);

export const selectIsDisableSharingOverDc = (state: RootState) => {
    return getConfig(state).disableSharingOverDc;
};

export const selectIsPdokHeaderEnabled = (state: RootState) => state.config.usePdokHeader;

export const selectIsYandexNet = (state: RootState) => Boolean(state.config.isYandexNet);

export const selectMessengerBackendPreset = (state: RootState) => state.config.messengerBackendPreset;

export const selectSlotPluginsConfigUrl = (state: RootState) => state.config.slotPluginsConfigUrl;

export const selectTeleMessengerRedirectInfo = (state: RootState) => state.config.teleMessengerRedirectInfo;

export const selectTeleMessengerRedirectUrl = (state: RootState) => selectTeleMessengerRedirectInfo(state)?.url;

export const selectTeleMessengerActualRedirectUrl = (state: RootState) => {
    const redirectInfo = selectTeleMessengerRedirectInfo(state);
    if (!redirectInfo) {
        return;
    }
    const { url, withAppendingHash } = redirectInfo;

    if (!withAppendingHash) {
        return url;
    }

    let hash = withAppendingHash ? window.location.hash : '';
    if (hash.length && hash[0] === '#') {
        hash = hash.slice(1);
    }
    return `${url}/${hash}`;
};

export const selectSelectedUid = (state: RootState) => state.config.selectedUid;

export const selectSelectedLogin = (state: RootState) => state.config.selectedLogin;

export const selectIsBroadcastEndEnabled = (state: RootState) =>
    Boolean(getExperimentsFlags(state).broadcast_end_enabled);

export const selectIsReconnectFlowV2Enabled = (state: RootState) =>
    Boolean(getExperimentsFlags(state)?.telemost_web_update_reconnect_flow);

export const selectIsTelemostDesktopIntegration = (state: RootState) => Boolean(state.config.isTelemostDesktopApp);

export const selectClientCookiesDomain = (state: RootState) => state.config.clientCookiesDomain;

export const selectGlobalBarInitData = (state: RootState) => state.config.globalBarInitData;

export const selectQuerySettings = (state: RootState) => state.config.querySettings;

export const selectQueryDeviceProcessorSettings = (state: RootState) => state.config.querySettings?.deviceProcessors;
